/**
 * Six-Box impersonation ("view as").
 *
 * Two independent dimensions, either or both:
 *
 *   - PERSON (`email`) — scope reads as if that individual were calling.
 *     Use when reproducing a specific person's bug report.
 *   - ROLE (`role`)   — scope reads at that tier while keeping your own
 *     identity. Use to answer "what does a manager actually see?" without
 *     having to pick a particular manager out of the directory.
 *
 * Arul 2026-08-21 — the role dimension is new. Carolyn's original
 * 2026-05-15 version was person-only, which made "audit what each tier can
 * reach" a game of guessing which colleague happened to sit in the tier you
 * wanted to inspect.
 *
 * The server is the authority on every gate — impersonator allowlist, the
 * CEO exclusion, and the tier ceiling (you may never assume a tier above
 * your own). See apps/api/src/domains/sixBox/middleware.ts. The UI mirrors
 * those rules so the controls are honest, but a hand-crafted request is
 * still refused.
 *
 * Writes are blocked server-side (403 IMPERSONATION_READ_ONLY) whenever any
 * lens is active; the UI gates write actions defensively too.
 *
 * State lives in sessionStorage so a hard refresh keeps the lens (helps when
 * reproducing a manager's bug) but closing the tab resets to the real
 * identity. Subscribers fire on changes via a synthetic CustomEvent so the
 * banner re-renders without prop drilling.
 */

const STORAGE_KEY = 'atlas-sixbox-impersonate-as';
const ROLE_STORAGE_KEY = 'atlas-sixbox-impersonate-role';
const EVENT = 'atlas:sixbox-impersonate-change';

/**
 * Tiers that can be assumed. Mirrors IMPERSONATABLE_TIERS in
 * apps/api/src/domains/sixBox/middleware.ts — keep in sync.
 *
 * `director` is intentionally not here: no resolver ever returns it and the
 * API treats it identically to `manager`, so offering it would present a
 * lens nobody actually holds.
 */
export const IMPERSONATABLE_TIERS = [
  'hr_admin',
  'executive',
  'hr_calibrator',
  'manager',
] as const;

export type ImpersonatableTier = (typeof IMPERSONATABLE_TIERS)[number];

/** Human labels for the role chips. */
export const TIER_LABELS: Record<ImpersonatableTier, string> = {
  hr_admin: 'HR Admin',
  executive: 'Executive',
  hr_calibrator: 'HR Calibrator',
  manager: 'Manager',
};

/** One-line description of what each tier can reach, shown under the chips. */
export const TIER_DESCRIPTIONS: Record<ImpersonatableTier, string> = {
  hr_admin: 'Every active employee, all ratings, full edit rights.',
  executive: 'Whole company except Andrew’s direct reports (peer execs are hidden from each other).',
  hr_calibrator: 'Same rows as an executive, but ratings on the HR chain are masked.',
  manager: 'Their own downline only — direct reports, or the full chain below them.',
};

function isTier(value: string | null): value is ImpersonatableTier {
  return !!value && (IMPERSONATABLE_TIERS as ReadonlyArray<string>).includes(value);
}

export function getImpersonateAs(): string | null {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    return v && v.trim() ? v.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

export function getImpersonateRole(): ImpersonatableTier | null {
  try {
    const v = sessionStorage.getItem(ROLE_STORAGE_KEY);
    return isTier(v) ? v : null;
  } catch {
    return null;
  }
}

/** True when any lens is active — used to gate write actions in the UI. */
export function isImpersonating(): boolean {
  return getImpersonateAs() !== null || getImpersonateRole() !== null;
}

function notify(): void {
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function setImpersonateAs(email: string | null): void {
  try {
    if (email && email.trim()) {
      sessionStorage.setItem(STORAGE_KEY, email.trim().toLowerCase());
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
    notify();
  } catch {
    // sessionStorage unavailable (private mode) — ignore.
  }
}

export function setImpersonateRole(role: ImpersonatableTier | null): void {
  try {
    if (role) {
      sessionStorage.setItem(ROLE_STORAGE_KEY, role);
    } else {
      sessionStorage.removeItem(ROLE_STORAGE_KEY);
    }
    notify();
  } catch {
    // sessionStorage unavailable (private mode) — ignore.
  }
}

/**
 * Drop both dimensions at once.
 *
 * "Exit" must clear person AND role together. Clearing only one leaves the
 * user in a half-lens they did not ask for, with the banner still up and no
 * obvious way to read what they are actually looking at.
 */
export function clearImpersonation(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(ROLE_STORAGE_KEY);
    notify();
  } catch {
    // sessionStorage unavailable (private mode) — ignore.
  }
}

export function subscribeImpersonateAs(cb: () => void): () => void {
  const handler = () => cb();
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}
